/**
 * ScheduleDayCell — one day cell in the auto-scheduler week grid.
 * Extracted from AdminAutoScheduler.js to reduce complexity.
 */
import { format, isBefore, isSameDay } from 'date-fns';
import { Badge } from '@/components/ui/badge';

export default function ScheduleDayCell({ date, getScheduleForDate, onEdit }) {
  const { existing, generated, hasExisting, hasGenerated } = getScheduleForDate(date);
  const isPast = isBefore(date, new Date()) && !isSameDay(date, new Date());
  const isToday = isSameDay(date, new Date());
  const dayName = format(date, 'EEE');
  const dayNum = format(date, 'd');
  const dateStr = format(date, 'yyyy-MM-dd');

  const meals = hasExisting
    ? existing.items
    : (hasGenerated ? (generated.meal_details || generated.meals || []) : []);

  return (
    <div
      className={`
        min-h-[180px] p-3 rounded-lg border transition-all cursor-pointer
        ${isPast ? 'opacity-50 bg-muted' : 'hover:border-primary/50 hover:shadow-sm'}
        ${isToday ? 'border-primary ring-2 ring-primary/20' : 'border-border'}
        ${hasGenerated && !hasExisting ? 'bg-primary/5 border-primary/30' : 'bg-background'}
      `}
      onClick={() => !isPast && onEdit(date)}
      data-testid={`schedule-day-${dateStr}`}
    >
      {/* Day Header */}
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className={`text-xs font-medium ${isToday ? 'text-primary' : 'text-muted-foreground'}`}>
            {dayName}
          </p>
          <p className={`text-lg font-semibold ${isToday ? 'text-primary' : 'text-foreground'}`}>
            {dayNum}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          {hasExisting && (
            <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200">
              Published
            </Badge>
          )}
          {hasGenerated && !hasExisting && (
            <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
              Generated
            </Badge>
          )}
        </div>
      </div>

      {/* Meals List */}
      <div className="space-y-1">
        {meals.slice(0, 4).map((meal) => (
          <div key={meal.item_id || meal.name} className="text-xs text-muted-foreground truncate">
            • {meal.name}
          </div>
        ))}
        {meals.length > 4 && (
          <div className="text-xs text-primary font-medium">+{meals.length - 4} more</div>
        )}
        {meals.length === 0 && !isPast && (
          <div className="text-xs text-muted-foreground italic">No meals scheduled</div>
        )}
      </div>
      {/* Edit hint */}
      {!isPast && (
        <div className="mt-2 pt-2 border-t border-border/50">
          <p className="text-[10px] text-muted-foreground flex items-center gap-1">
            Click to edit
          </p>
        </div>
      )}
    </div>
  );
}
